import type { ViewController } from '@presentation/shell/NexxusShell';
import { SecureTenantStore } from '@infra/persistence/SecureTenantStore';
import { ToastService } from '@presentation/components/Toast';
import { previewDocument } from '@presentation/print/PdfService';
import {
  buildRapportGestionHTML,
  buildReglementInterieurHTML,
} from '@presentation/print/rapportTemplates';
import { esc, fmtDate, fmtFCFA } from '@presentation/shell/format';
import { canMutateRecords } from '@application/access/ViewAccessPolicy';
import { uuid } from '@shared/utils/uuid';
import { IRVMService } from '@domain/fiscalite/IRVMService';
import {
  bindCrudTable,
  crudActions,
  emptyRow,
  loadRegister,
  openRegisterForm,
  removeById,
  saveRegister,
  tenantIdOf,
  upsertById,
} from '@presentation/shell/localCrud';

function fillYears(select: HTMLSelectElement | null): void {
  if (!select || select.options.length > 0) return;
  const now = new Date().getFullYear();
  for (let y = now; y >= now - 5; y--) {
    const o = document.createElement('option');
    o.value = String(y);
    o.textContent = String(y);
    select.appendChild(o);
  }
}

export interface AssembleeLocal {
  id: string;
  date: string;
  type: string;
  objet: string;
  decisions: string;
  statut: string;
}

export interface ConventionLocal {
  id: string;
  date: string;
  partie: string;
  objet: string;
  montant: number;
  autorisee_par: string;
  statut: string;
}

export interface DistributionLocal {
  id: string;
  date: string;
  exercice: string;
  type: string;
  brut: number;
}

function guard(can: boolean): boolean {
  if (can) return true;
  ToastService.warning('Action réservée au gestionnaire RH et à l’administrateur');
  return false;
}

export const vieSocialeController: ViewController = async (section, ctx, shell) => {
  const tid = tenantIdOf(ctx);
  const canMutate = canMutateRecords(shell.permission);
  const rapportLegacy = `nexxus.viesociale.${tid}.rapport`;
  const riLegacy = `nexxus.viesociale.${tid}.ri`;

  const rappAnnee = section.querySelector<HTMLSelectElement>('#rapp-annee');
  fillYears(rappAnnee);

  const setVal = (id: string, v: string): void => {
    const el = section.querySelector<HTMLInputElement | HTMLTextAreaElement>(id);
    if (el) el.value = v;
  };
  const getVal = (id: string): string =>
    section.querySelector<HTMLInputElement | HTMLTextAreaElement>(id)?.value ?? '';

  const loadRapport = async (): Promise<void> => {
    const all = await SecureTenantStore.getJson<Record<string, Record<string, string>>>(
      tid,
      'viesociale.rapport',
      {},
      rapportLegacy,
    );
    const r = all[rappAnnee?.value ?? ''] ?? {};
    setVal('#rapp-activite', r['activite'] ?? '');
    setVal('#rapp-faits', r['faits'] ?? '');
    setVal('#rapp-perspectives', r['perspectives'] ?? '');
    setVal('#rapp-rse', r['rse'] ?? '');
    setVal('#rapp-invest', r['invest'] ?? '');
  };
  await loadRapport();

  const ri = await SecureTenantStore.getJson<Record<string, string>>(
    tid,
    'viesociale.ri',
    {},
    riLegacy,
  );
  setVal('#ri-date', ri['date'] ?? '');
  setVal('#ri-discipline', ri['discipline'] ?? '');
  setVal('#ri-hygiene', ri['hygiene'] ?? '');
  setVal('#ri-sanctions', ri['sanctions'] ?? '');

  let ags = await loadRegister<AssembleeLocal>(tid, 'viesociale.ag');
  let conventions = await loadRegister<ConventionLocal>(tid, 'viesociale.conventions');
  let distributions = await loadRegister<DistributionLocal>(tid, 'viesociale.distributions');

  const renderAg = (): void => {
    const tbody = section.querySelector('#ag-table tbody');
    if (!tbody) return;
    tbody.innerHTML =
      ags.length === 0
        ? emptyRow(6, 'Aucune assemblée enregistrée.')
        : ags
            .map(
              (a) => `<tr>
          <td>${esc(fmtDate(a.date))}</td>
          <td>${esc(a.type)}</td>
          <td>${esc(a.objet)}</td>
          <td>${esc(a.decisions)}</td>
          <td><span class="pill">${esc(a.statut)}</span></td>
          <td class="num">${crudActions(a.id)}</td>
        </tr>`,
            )
            .join('');
  };
  const renderConv = (): void => {
    const tbody = section.querySelector('#conv-table tbody');
    if (!tbody) return;
    tbody.innerHTML =
      conventions.length === 0
        ? emptyRow(7, 'Aucune convention enregistrée.')
        : conventions
            .map(
              (c) => `<tr>
          <td>${esc(fmtDate(c.date))}</td>
          <td>${esc(c.partie)}</td>
          <td>${esc(c.objet)}</td>
          <td class="num">${esc(fmtFCFA(c.montant))}</td>
          <td>${esc(c.autorisee_par)}</td>
          <td><span class="pill">${esc(c.statut)}</span></td>
          <td class="num">${crudActions(c.id)}</td>
        </tr>`,
            )
            .join('');
  };
  const renderDistrib = (): void => {
    const tbody = section.querySelector('#distrib-table tbody');
    if (!tbody) return;
    tbody.innerHTML =
      distributions.length === 0
        ? emptyRow(7, 'Aucune distribution enregistrée.')
        : distributions
            .map((d) => {
              const typeIrvm =
                d.type === 'obligation' || d.type === 'obligations' ? 'obligations' : 'dividendes';
              const { irvm, net } = IRVMService.calculer(d.brut, typeIrvm);
              return `<tr>
          <td>${esc(fmtDate(d.date))}</td>
          <td>${esc(d.exercice)}</td>
          <td>${esc(d.type)}</td>
          <td class="num">${esc(fmtFCFA(d.brut))}</td>
          <td class="num">${esc(fmtFCFA(irvm))}</td>
          <td class="num">${esc(fmtFCFA(net))}</td>
          <td class="num">${crudActions(d.id)}</td>
        </tr>`;
            })
            .join('');
  };
  renderAg();
  renderConv();
  renderDistrib();

  const openAg = (existing?: AssembleeLocal): void => {
    if (!guard(canMutate)) return;
    openRegisterForm({
      parent: section,
      title: existing ? 'Modifier l’AG' : 'Nouvelle AG / PV',
      fields: [
        { kind: 'date', name: 'date', label: 'Date', required: true },
        {
          kind: 'select',
          name: 'type',
          label: 'Type',
          options: [
            { value: 'AGO', label: 'AGO' },
            { value: 'AGE', label: 'AGE' },
          ],
        },
        { kind: 'text', name: 'objet', label: 'Objet', required: true },
        { kind: 'text', name: 'decisions', label: 'Décisions', required: true },
        {
          kind: 'select',
          name: 'statut',
          label: 'Statut',
          options: [
            { value: 'convoquee', label: 'Convoquée' },
            { value: 'tenue', label: 'Tenue' },
            { value: 'archivee', label: 'Archivée' },
          ],
        },
      ],
      values: existing,
      onSubmit: async (data) => {
        const row: AssembleeLocal = {
          id: existing?.id ?? uuid('ag'),
          date: String(data['date']),
          type: String(data['type']),
          objet: String(data['objet']),
          decisions: String(data['decisions']),
          statut: String(data['statut']),
        };
        ags = upsertById(ags, row);
        await saveRegister(tid, 'viesociale.ag', ags);
        renderAg();
      },
    });
  };

  const openConv = (existing?: ConventionLocal): void => {
    if (!guard(canMutate)) return;
    openRegisterForm({
      parent: section,
      title: existing ? 'Modifier la convention' : 'Nouvelle convention',
      fields: [
        { kind: 'date', name: 'date', label: 'Date', required: true },
        { kind: 'text', name: 'partie', label: 'Partie liée', required: true },
        { kind: 'text', name: 'objet', label: 'Objet', required: true },
        { kind: 'number', name: 'montant', label: 'Montant', min: 0 },
        { kind: 'text', name: 'autorisee_par', label: 'Autorisée par', required: true },
        {
          kind: 'select',
          name: 'statut',
          label: 'Statut',
          options: [
            { value: 'proposee', label: 'Proposées' },
            { value: 'autorisee', label: 'Autorisée' },
            { value: 'refusee', label: 'Refusée' },
          ],
        },
      ],
      values: existing,
      onSubmit: async (data) => {
        const row: ConventionLocal = {
          id: existing?.id ?? uuid('conv'),
          date: String(data['date']),
          partie: String(data['partie']),
          objet: String(data['objet']),
          montant: Number(data['montant'] ?? 0),
          autorisee_par: String(data['autorisee_par']),
          statut: String(data['statut']),
        };
        conventions = upsertById(conventions, row);
        await saveRegister(tid, 'viesociale.conventions', conventions);
        renderConv();
      },
    });
  };

  const openDistrib = (existing?: DistributionLocal): void => {
    if (!guard(canMutate)) return;
    openRegisterForm({
      parent: section,
      title: existing ? 'Modifier la distribution' : 'Nouvelle distribution',
      fields: [
        { kind: 'date', name: 'date', label: 'Date', required: true },
        { kind: 'text', name: 'exercice', label: 'Exercice', required: true },
        {
          kind: 'select',
          name: 'type',
          label: 'Type',
          options: [
            { value: 'dividende', label: 'Dividende (IRCM 20 %)' },
            { value: 'obligation', label: 'Obligation à 5 ans et plus (IRCM 10 %)' },
            { value: 'acompte', label: 'Acompte (IRCM 20 %)' },
          ],
        },
        { kind: 'number', name: 'brut', label: 'Brut (FCFA)', min: 0 },
      ],
      values: existing,
      onSubmit: async (data) => {
        const row: DistributionLocal = {
          id: existing?.id ?? uuid('div'),
          date: String(data['date']),
          exercice: String(data['exercice']),
          type: String(data['type']),
          brut: Number(data['brut'] ?? 0),
        };
        distributions = upsertById(distributions, row);
        await saveRegister(tid, 'viesociale.distributions', distributions);
        renderDistrib();
      },
    });
  };

  bindCrudTable(
    section.querySelector('#ag-table tbody'),
    section,
    (id) => openAg(ags.find((x) => x.id === id)),
    async (id) => {
      ags = removeById(ags, id);
      await saveRegister(tid, 'viesociale.ag', ags);
      renderAg();
    },
    canMutate,
  );
  bindCrudTable(
    section.querySelector('#conv-table tbody'),
    section,
    (id) => openConv(conventions.find((x) => x.id === id)),
    async (id) => {
      conventions = removeById(conventions, id);
      await saveRegister(tid, 'viesociale.conventions', conventions);
      renderConv();
    },
    canMutate,
  );
  bindCrudTable(
    section.querySelector('#distrib-table tbody'),
    section,
    (id) => openDistrib(distributions.find((x) => x.id === id)),
    async (id) => {
      distributions = removeById(distributions, id);
      await saveRegister(tid, 'viesociale.distributions', distributions);
      renderDistrib();
    },
    canMutate,
  );

  const w = window as unknown as Record<string, unknown>;
  w['openAGModal'] = () => openAg();
  w['openConventionModal'] = () => openConv();
  w['openDistributionModal'] = () => openDistrib();
  w['renderRapportGestion'] = () => {
    void loadRapport();
  };
  w['enregistrerRapportGestion'] = async () => {
    if (!guard(canMutate)) return;
    const annee = rappAnnee?.value ?? String(new Date().getFullYear());
    const all = await SecureTenantStore.getJson<Record<string, Record<string, string>>>(
      tid,
      'viesociale.rapport',
      {},
      rapportLegacy,
    );
    all[annee] = {
      activite: getVal('#rapp-activite'),
      faits: getVal('#rapp-faits'),
      perspectives: getVal('#rapp-perspectives'),
      rse: getVal('#rapp-rse'),
      invest: getVal('#rapp-invest'),
    };
    await SecureTenantStore.setJson(tid, 'viesociale.rapport', all, rapportLegacy);
    ToastService.success(`Rapport de gestion ${annee} enregistré`);
  };
  w['enregistrerRI'] = async () => {
    if (!guard(canMutate)) return;
    await SecureTenantStore.setJson(
      tid,
      'viesociale.ri',
      {
        date: getVal('#ri-date'),
        discipline: getVal('#ri-discipline'),
        hygiene: getVal('#ri-hygiene'),
        sanctions: getVal('#ri-sanctions'),
      },
      riLegacy,
    );
    ToastService.success('Règlement intérieur enregistré');
  };
  w['imprimerRapportGestion'] = () => {
    const annee = rappAnnee?.value ?? '';
    const html = buildRapportGestionHTML(
      annee,
      {
        activite: getVal('#rapp-activite'),
        faits: getVal('#rapp-faits'),
        perspectives: getVal('#rapp-perspectives'),
        rse: getVal('#rapp-rse'),
        invest: getVal('#rapp-invest'),
      },
      ctx.tenantContext.tryGetTenant(),
    );
    previewDocument(html, `rapport-gestion-${annee}.pdf`, `Rapport de gestion ${annee}`);
  };
  w['imprimerRI'] = () => {
    const html = buildReglementInterieurHTML(
      {
        date: getVal('#ri-date'),
        discipline: getVal('#ri-discipline'),
        hygiene: getVal('#ri-hygiene'),
        sanctions: getVal('#ri-sanctions'),
      },
      ctx.tenantContext.tryGetTenant(),
    );
    previewDocument(html, 'reglement-interieur.pdf', 'Règlement intérieur');
  };
};
